/**
 * Reverse lookup for conjugated verbs.
 *
 * Nouns are analysed by guessing from an ending table and verifying by
 * re-deriving (`analyze.ts`). That cannot work for verbs: a declined noun keeps
 * its stem visible, but a conjugated verb does not — *gacchati* shares little
 * with √gam and *jagmuḥ* less — and the root itself mutates, so an ending table
 * only ever covers the roots it was built from. Measured, it reaches 61%
 * coverage from 60 exemplar roots and 84% from 226, converging on this index
 * rather than generalising past it.
 *
 * So the forms are precomputed instead: every root of the Dhātupāṭha through the
 * lakāras a reader actually meets. The file is ~3 MB of text, 743 KB over the
 * wire, and is fetched only when a search finds nothing as a nominal — which is
 * the great majority of lookups, so most sessions never pay for it.
 */

import { Gana, LAKARAS, Lakara, PURUSHAS, Purusha, VACANAS, Vacana } from "./verb-types";

/**
 * The lakāras in the index, in the order their numeric tag refers to. Changing
 * this list invalidates `data/verb-forms.txt`; regenerate it.
 */
export const INDEXED_LAKARAS: Lakara[] = [
    "Lat", "Lan", "Lot", "VidhiLin", "Lit", "Lrt", "Lun",
    // The remaining three are rarer but a reader still meets them.
    "Lut", "AshirLin", "Lrn",
];
export const INDEXED_PURUSHAS: Purusha[] = ["Prathama", "Madhyama", "Uttama"];
export const INDEXED_VACANAS: Vacana[] = ["Eka", "Dvi", "Bahu"];

/**
 * The voices indexed. Passive forms look nothing like their active counterparts
 * — *gamyate* beside *gacchati* — so a reader who meets one has no way to reach
 * the root without it.
 */
export const INDEXED_PRAYOGAS = ["Kartari", "Karmani"] as const;
export type IndexedPrayoga = typeof INDEXED_PRAYOGAS[number];

export type VerbAnalysis = {
    /** The root's surface form, e.g. "gam". */
    root: string;
    /** Its aupadeśika form, for handing back to the engine. */
    aupadeshika: string;
    gana: Gana;
    /** Dhātupāṭha reference. */
    code: string;
    /** The Dhātupāṭha gloss. */
    artha: string;
    lakara: Lakara;
    purusha: Purusha;
    vacana: Vacana;
    /** Active or passive. */
    prayoga: IndexedPrayoga;
};

type RootEntry = {
    code: string; aupadeshika: string; gana: Gana; forms: string[]; artha: string;
};

let forms: Map<string, string> | null = null;
let roots: RootEntry[] | null = null;
let pending: Promise<void> | null = null;

/**
 * Expands the front-coded file into a lookup table.
 *
 * Each line stores how many leading characters it shares with the previous one,
 * then the remainder — the paradigms share long prefixes, so this roughly halves
 * the file. The tags are kept as the raw string and only parsed on a hit, which
 * avoids building 180,000 objects for a lookup that touches one.
 */
const parse = (text: string): Map<string, string> => {
    const out = new Map<string, string>();
    let prev = "";
    let i = 0;
    const n = text.length;

    while (i < n) {
        let lineEnd = text.indexOf("\n", i);
        if (lineEnd === -1) lineEnd = n;

        const tab = text.indexOf("\t", i);
        if (tab === -1 || tab > lineEnd) { i = lineEnd + 1; continue; }

        // leading digits = shared prefix length
        let j = i;
        let shared = 0;
        while (j < tab) {
            const c = text.charCodeAt(j);
            if (c < 48 || c > 57) break;
            shared = shared * 10 + (c - 48);
            j += 1;
        }
        const form = prev.slice(0, shared) + text.slice(j, tab);
        out.set(form, text.slice(tab + 1, lineEnd));
        prev = form;
        i = lineEnd + 1;
    }
    return out;
};

/** Loads the index, once. */
export const loadVerbForms = (): Promise<void> => {
    if (forms) return Promise.resolve();
    if (pending) return pending;

    pending = Promise.all([
        import("./data/verb-forms.txt"),
        import("./data/roots.json"),
    ])
        .then(([text, rootData]) => {
            const raw = ((text as any).default ?? text) as string;
            forms = parse(raw);
            roots = (((rootData as any).default ?? rootData) as unknown) as RootEntry[];
        })
        .catch(() => {
            // Do not remember a failed fetch; see the note in vidyut-engine.ts.
            pending = null;
        });

    return pending;
};

/** Whether the index is resident. */
export const verbFormsLoaded = (): boolean => forms !== null;

/**
 * Every root and cell that produces this form. Returns nothing until
 * `loadVerbForms()` has resolved.
 */
export const lookupVerb = (surface: string): VerbAnalysis[] => {
    if (!forms || !roots) return [];
    const tags = forms.get(surface.trim());
    if (!tags) return [];

    const out: VerbAnalysis[] = [];
    for (const tag of tags.split(";")) {
        const [ri, li, pi, vi, yi] = tag.split(",").map(Number);
        const r = roots[ri];
        if (!r) continue;
        out.push({
            root: r.forms[0],
            aupadeshika: r.aupadeshika,
            gana: r.gana,
            code: r.code,
            artha: r.artha,
            lakara: INDEXED_LAKARAS[li],
            purusha: INDEXED_PURUSHAS[pi],
            vacana: INDEXED_VACANAS[vi],
            prayoga: INDEXED_PRAYOGAS[yi ?? 0] ?? "Kartari",
        });
    }
    return out;
};

/** A readable label for an analysis, e.g. "present 3rd singular". */
export const describeVerbAnalysis = (v: VerbAnalysis): string => {
    const l = LAKARAS.find(x => x.id === v.lakara)?.gloss ?? v.lakara;
    const p = PURUSHAS.find(x => x.id === v.purusha)?.gloss ?? v.purusha;
    const n = VACANAS.find(x => x.id === v.vacana)?.gloss ?? v.vacana;
    const voice = v.prayoga === "Karmani" ? " passive" : "";
    return `${l}${voice} ${p} ${n}`;
};
